<template>
  <section
    class="m-page-section"
    :class="{
      'm-page-section--full-height': fullHeight,
      [`m-page-section--vertical-spacing-${verticalSpacing}`]: verticalSpacing,
      'm-page-section--responsive': responsive,
      [`m-page-section--${type}`]: type !== 'plain',
      [`m-page-section--${contentAlignMobile}`]: contentAlignMobile !== 'top',
      [`m-page-section--${contentAlignDesktop}`]:
        contentAlignDesktop !== 'left',
      'm-page-section--limit-height': limitHeight,
    }"
  >
    <div class="m-page-section__content" :style="reducedPadding">
      <slot />
    </div>
    <div v-if="$slots.image" class="m-page-section__image">
      <slot name="image" />
    </div>
  </section>
</template>

<script>
/**
 * A basic component to help with page layout.
 * Has some default padding that is configurable via props.
 */
import spacings from './verticalSpacings'

export default {
  name: 'MPageSection',
  props: {
    type: {
      type: String,
      default: 'plain',
      validator: (prop) =>
        ['plain', 'illustrated', 'full-bleed', 'half-bleed'].includes(prop),
    },
    /**
     * Modify the top & bottom padding
     */
    verticalSpacing: {
      type: String,
      default: 'xl',
      validator: (size) => spacings.includes(size),
    },
    /**
     * Modify where vertical padding is applied
     */
    verticalSpacingPlacement: {
      type: String,
      default: 'both',
      validator: (size) => ['both', 'top', 'bottom'].includes(size),
    },
    /**
     * Force the section to fill the height of it's container
     */
    fullHeight: {
      type: Boolean,
      default: false,
    },
    /**
     * Bump the spacing up one unit on desktop and tablet
     */
    responsive: {
      type: Boolean,
      default: false,
    },
    /**
     * Align the content vs the image on
     * mobile, up to tablet
     */
    contentAlignMobile: {
      type: String,
      default: 'top',
      validator: (prop) => ['top', 'bottom'].includes(prop),
    },
    /**
     * Align the content vs the image on desktop
     * and tablets
     */
    contentAlignDesktop: {
      type: String,
      default: 'left',
      validator: (prop) => ['left', 'right', 'full-width'].includes(prop),
    },
    /**
     * Limit the section height to the text content height, cropping the image
     */
    limitHeight: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    reducedPadding() {
      switch (this.verticalSpacingPlacement) {
        case 'top':
          return 'padding-bottom: 0;'
        case 'bottom':
          return 'padding-top: 0;'
        default:
          return ''
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use 'sass:math';
@use 'sass:list';

@use '../../../sass/framework/variables/screen';
@use '../../../sass/framework/variables/spacing';
@use '../../../sass/framework/mixins/breakpoint';

.m-page-section {
  $self: &;
  background: var(--skin-background);
  color: var(--skin-color);

  &__content {
    @include breakpoint.breakpoint(tablet-up) {
      align-self: center;
    }
  }

  &__image {
    :deep(img) {
      display: block;
      height: auto;
      width: 100%;
    }
  }

  &--vertical-spacing {
    @each $key, $value in spacing.$sizes {
      &-#{$key} {
        .m-page-section__content {
          padding-block: $value;
        }

        @include breakpoint.breakpoint(tablet-up) {
          // Bump spacing up by one on tablet/desktop
          // if using responsive spacing
          &.m-page-section--responsive {
            .m-page-section__content {
              $keys: map.keys(spacing.$sizes);
              $index: list.index($keys, $key);
              // Last key just stays the same
              @if ($index <= list.length($keys) - 1) {
                // Otherwise return the next one
                $next-key: list.nth($keys, $index + 1);
                $spacing-amount: map.get(spacing.$sizes, $next-key);
                padding-block: $spacing-amount;
              }
            }
          }
        }
      }
    }
  }

  &--illustrated {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;

    #{$self}__image {
      :deep(img) {
        display: block;
        margin: auto;
        max-width: 100%;
        object-fit: cover;
      }
    }
  }

  &--half-bleed {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, auto);

    @include breakpoint.breakpoint(tablet-up) {
      $page-section-half-bleed-column: min(
        50%,
        #{math.div(screen.$widescreen, 2)}
      );
      grid-template-columns: 1fr $page-section-half-bleed-column $page-section-half-bleed-column 1fr;
      grid-template-rows: 1fr;

      #{$self}__content {
        grid-column: 2 / 3;
      }

      #{$self}__image {
        grid-column: 3 / 5;

        :deep(img) {
          grid-row: 1 / 1;
          height: 100%;
          object-fit: cover;
        }
      }
    }
  }

  &--full-bleed {
    display: grid;

    #{$self}__content {
      z-index: 2;
    }

    @include breakpoint.breakpoint(tablet-up) {
      $page-section-full-bleed-column: min(
        50%,
        #{math.div(screen.$widescreen, 2)}
      );
      grid-template-columns: 1fr $page-section-full-bleed-column $page-section-full-bleed-column 1fr;

      #{$self}__content {
        grid-column: 2 / 3;
        grid-row: 1 / 2;
        z-index: 2;
      }

      #{$self}__image {
        grid-column: 1 / 5;
        grid-row: 1 / 2;

        :deep(img) {
          grid-row: 1 / 1;
          height: 100%;
          object-fit: cover;
        }
      }
    }
  }

  // Content alignment
  // Mobile
  &--bottom {
    @include breakpoint.breakpoint(large-mobile-down) {
      #{$self}__content {
        grid-row: 2;
        order: 2;
      }
    }
  }

  // Desktop
  &--right {
    @include breakpoint.breakpoint(tablet-up) {
      // The image only gets moved on half bleed type
      &#{$self}--half-bleed {
        #{$self}__image {
          grid-column: 1 / 3;
        }
      }

      #{$self}__content {
        grid-column: 3 / 4;
        grid-row: 1;
        order: 1;
      }
    }
  }

  &--full-width {
    &#{$self}--full-bleed {
      #{$self}__image {
        grid-column: 1 / 5;
        grid-row: 1;

        @include breakpoint.breakpoint(large-mobile-down) {
          :deep(img) {
            height: 100%;
            object-fit: cover;
          }
        }
      }

      #{$self}__content {
        grid-column: 1 / 5;
        grid-row: 1;
        order: 1;
      }
    }

    @include breakpoint.breakpoint(tablet-up) {
      // The image only gets moved on half bleed type
      &#{$self}--half-bleed {
        #{$self}__image {
          grid-column: 3 / 5;
          grid-row: 1 / 2;
        }
      }
    }
  }

  &--limit-height {
    @include breakpoint.breakpoint(tablet-up) {
      #{$self}__image {
        // Fix for Safari img height collapsing
        :deep(picture) {
          display: block;
          height: 100%;
        }
        :deep(img) {
          height: 0;
          min-height: 100%;
        }
      }
    }
  }
}

// Full height page sections should be 100dvh
// as standard, but if there is a navbar, we
// need to reduce that by the height of the navbar
.m-page-section--full-height {
  min-height: 100dvh;
}

body:has(.m-navbar) .m-page-section--full-height {
  min-height: calc(100dvh - var(--header-height));
}
</style>
